import { useQueryClient } from "@tanstack/react-query";
import { doc, getFirestore, runTransaction } from "firebase/firestore";
import { type FormEvent, useRef, useState } from "react";
import { AiOutlineUser } from "react-icons/ai";
import { BsTelephone } from "react-icons/bs";
import { FaPercent, FaRegAddressBook, FaRoute } from "react-icons/fa";
import { HiOutlinePhotograph } from "react-icons/hi";
import { HiOutlineIdentification } from "react-icons/hi2";
import { IoIosClose } from "react-icons/io";
import { IoLocationOutline, IoStorefrontOutline } from "react-icons/io5";
import { MoonLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useClientesActions, useClientesState } from "../../../../../context/AppContext";
import { queryKeys } from "../../../../../queries/queryKeys";
import {
  calcularFechaFinalCredito,
  calcularPagoDiarioCredito,
  formatearFechaActualIso,
  leerArchivoComoPreview,
  OPCIONES_FORMA_PAGO_CREDITO,
} from "../../../../../utils/clienteCredito";
import {
  aplicarMovimientoARutaLocal,
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
} from "../../../../../utils/rutaMovimientos";
import { guardarClienteEnFirebase, uploadImage, uploadImageTienda } from "../../firebaseFunctions";
import { CampoEntrada } from "./CampoEntrada";

const OPCIONES_FORMA_PAGO = [...OPCIONES_FORMA_PAGO_CREDITO];
const MENSAJE_CAMPO_OBLIGATORIO = "Este campo es obligatorio";

const esCampoValidable = (
  element: EventTarget | null,
): element is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLSelectElement ||
  element instanceof HTMLTextAreaElement;

const crearDatosClienteIniciales = (fechaUltimoAbono) => ({
  image: null,
  imageTienda: null,
  cpf: "",
  nombreCliente: "",
  direccionCobro: "",
  telefono: "",
  direccion: "",
  descripcion: "",
  ubicacion: "",
  valorPrestamo: 1,
  porcentajeInteres: 0,
  cuotasPactadas: 1,
  pagoDiario: 0,
  abono: 0,
  cuotasPagadas: 0,
  formaDePago: "diario",
  fechaFinal: "",
  valorPico: 0,
  fechaUltimoAbono,
  totalAbono: 0,
  cuotasAtrasadas: 0,
  actualizado: new Date().toDateString(),
  historialPagos: [],
});

const obtenerErrorValidacion = (datosCliente) => {
  if (datosCliente.valorPrestamo <= 0) {
    return "El VALOR PRESTAMO debe ser mayor que cero (0)";
  }

  if (datosCliente.porcentajeInteres <= 0) {
    return "El PORCENTAJE DE INTERES debe ser mayor que cero (0)";
  }

  if (datosCliente.cuotasPactadas <= 0) {
    return "Las CUOTAS PACTADAS deben ser mayor que cero (0)";
  }

  return null;
};

const mostrarMensajeCampoObligatorio = (event: FormEvent<HTMLFormElement>) => {
  const campo = event.target;

  if (!esCampoValidable(campo)) {
    return;
  }

  campo.setCustomValidity(campo.validity.valueMissing ? MENSAJE_CAMPO_OBLIGATORIO : "");
};

const limpiarMensajeCampoObligatorio = (event: FormEvent<HTMLFormElement>) => {
  const campo = event.target;

  if (!esCampoValidable(campo)) {
    return;
  }

  campo.setCustomValidity("");
};

export const CrearClienteModalNew = ({
  setisModalCreateClienteNew,
  usuarioRuta,
  setUsuarioRuta,
}) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [imageTiendaPreview, setImageTiendaPreview] = useState(null);
  const [fechaInicialState, setFechaInicialState] = useState(formatearFechaActualIso());
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { infoClientes } = useClientesState();
  const { setInfoClientes } = useClientesActions();
  const queryClient = useQueryClient();

  const fechaUltimoAbono = new Date().toDateString();
  const [datosCliente, setDatosCliente] = useState(() =>
    crearDatosClienteIniciales(fechaUltimoAbono),
  );
  const imageGalleryInputRef = useRef<HTMLInputElement | null>(null);
  const imageCameraInputRef = useRef<HTMLInputElement | null>(null);
  const imageTiendaGalleryInputRef = useRef<HTMLInputElement | null>(null);
  const imageTiendaCameraInputRef = useRef<HTMLInputElement | null>(null);

  const [selectorFotoActivo, setSelectorFotoActivo] = useState<"cliente" | "tienda" | null>(null);

  const actualizarCredito = (updater) => {
    setDatosCliente((current) => {
      const next = updater(current);
      const valorPrestamo = Number(next.valorPrestamo ?? 0);
      const porcentajeInteres = Number(next.porcentajeInteres ?? 0);
      const cuotasPactadas = Number(next.cuotasPactadas ?? 0);

      return {
        ...next,
        fechaFinal: calcularFechaFinalCredito({
          cuotasPactadas,
          fechaBase: fechaInicialState,
          formaDePago: next.formaDePago,
        }),
        pagoDiario: calcularPagoDiarioCredito(valorPrestamo, porcentajeInteres, cuotasPactadas),
      };
    });
  };

  const handleValorPrestamoChange = (event) => {
    const valorPrestamo = parseInt(event.target.value, 10);

    actualizarCredito((current) => ({
      ...current,
      valorPrestamo: Number.isNaN(valorPrestamo) ? 0 : valorPrestamo,
    }));
  };

  const handlePorcentajeInteresChange = (event) => {
    const porcentajeInteres = parseFloat(event.target.value);

    actualizarCredito((current) => ({
      ...current,
      porcentajeInteres: Number.isNaN(porcentajeInteres) ? 0 : porcentajeInteres,
    }));
  };

  const handleCuotasPactadasChange = (event) => {
    const cuotasPactadas = parseInt(event.target.value, 10);

    actualizarCredito((current) => ({
      ...current,
      cuotasPactadas: Number.isNaN(cuotasPactadas) ? 0 : cuotasPactadas,
    }));
  };

  const handleFormaDePagoChange = (event) => {
    const formaDePago = event.target.value;

    actualizarCredito((current) => ({
      ...current,
      formaDePago,
    }));
  };

  const handleFechaInicialChange = (event) => {
    const nuevaFechaInicial = event.target.value;
    setFechaInicialState(nuevaFechaInicial);

    setDatosCliente((current) => ({
      ...current,
      fechaFinal: calcularFechaFinalCredito({
        cuotasPactadas: Number(current.cuotasPactadas ?? 0),
        fechaBase: nuevaFechaInicial,
        formaDePago: current.formaDePago,
      }),
    }));
  };

  const obtenerUbicacionActual = () => {
    if (!navigator.geolocation) {
      console.error("La geolocalizacion no esta soportada por este navegador.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        setDatosCliente((current) => ({
          ...current,
          ubicacion: `${latitude}, ${longitude}`,
        }));
      },
      (error) => {
        console.error("Error obteniendo la ubicacion:", error);
      },
    );
  };

  const abrirSelectorFoto = (tipo: "cliente" | "tienda") => {
    setSelectorFotoActivo(tipo);
  };

  const tomarFoto = () => {
    if (selectorFotoActivo === "cliente") {
      imageCameraInputRef.current?.click();
    }

    if (selectorFotoActivo === "tienda") {
      imageTiendaCameraInputRef.current?.click();
    }

    setSelectorFotoActivo(null);
  };

  const cargarDesdeGaleria = () => {
    if (selectorFotoActivo === "cliente") {
      imageGalleryInputRef.current?.click();
    }

    if (selectorFotoActivo === "tienda") {
      imageTiendaGalleryInputRef.current?.click();
    }

    setSelectorFotoActivo(null);
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setDatosCliente((current) => ({
      ...current,
      image: file,
    }));
    leerArchivoComoPreview(file, setImagePreview);
  };

  const handleImageTiendaChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setDatosCliente((current) => ({
      ...current,
      imageTienda: file,
    }));
    leerArchivoComoPreview(file, setImageTiendaPreview);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setDatosCliente((current) => ({
      ...current,
      image: null,
    }));

    if (imageGalleryInputRef.current) {
      imageGalleryInputRef.current.value = "";
    }

    if (imageCameraInputRef.current) {
      imageCameraInputRef.current.value = "";
    }
  };

  const handleRemoveImageTienda = () => {
    setImageTiendaPreview(null);
    setDatosCliente((current) => ({
      ...current,
      imageTienda: null,
    }));

    if (imageTiendaGalleryInputRef.current) {
      imageTiendaGalleryInputRef.current.value = "";
    }

    if (imageTiendaCameraInputRef.current) {
      imageTiendaCameraInputRef.current.value = "";
    }
  };

  const crearCliente = async (event) => {
    event.preventDefault();
    setIsSubmiting(true);

    const errorValidacion = obtenerErrorValidacion(datosCliente);

    if (errorValidacion) {
      setIsSubmiting(false);
      toast.error(errorValidacion);
      return;
    }

    try {
      const [imageTiendaUrl, imageUrl] = await Promise.all([
        uploadImageTienda({
          imageTienda: datosCliente.imageTienda,
          descripcion: datosCliente.descripcion,
        }),
        uploadImage({
          image: datosCliente.image,
          nombre: datosCliente.nombreCliente,
        }),
      ]);

      const db = getFirestore();
      const rutaRef = doc(db, "admin_users", usuarioRuta.adminUid, "rutas", usuarioRuta.uid);

      const valorPrestamo = Number(datosCliente.valorPrestamo);
      const porcentajeInteres = Number(datosCliente.porcentajeInteres);
      const cuotasPactadas = Number(datosCliente.cuotasPactadas);
      const datosClienteNormalizados = {
        ...datosCliente,
        fechaFinal: calcularFechaFinalCredito({
          cuotasPactadas,
          fechaBase: fechaInicialState,
          formaDePago: datosCliente.formaDePago,
        }),
        pagoDiario: Math.ceil(
          calcularPagoDiarioCredito(valorPrestamo, porcentajeInteres, cuotasPactadas),
        ),
      };

      const fechaMovimiento = new Date();
      const movimientoRef = crearMovimientoRutaRef(db, usuarioRuta.adminUid, usuarioRuta.uid);
      const resultadoTransaccion = await runTransaction(db, async (transaction) => {
        const rutaSnapshot = await transaction.get(rutaRef);
        const rutaData = rutaSnapshot.data();

        if (!rutaData) {
          throw new Error("No se encontro la informacion de la ruta");
        }

        const saldoAnterior = Number(rutaData.saldoInicial ?? 0);
        const saldoNuevo = saldoAnterior - valorPrestamo;
        const nuevoMovimiento = crearMovimientoRutaData({
          descripcion: "prestamo",
          fecha: fechaMovimiento,
          monto: valorPrestamo,
          origen: "crear_cliente",
          responsable: rutaData.responsable ?? "Admin",
          saldoDespues: saldoNuevo,
        });
        const rutaUpdates = construirActualizacionRutaConMovimiento(
          rutaData,
          nuevoMovimiento,
          saldoNuevo,
        );

        transaction.set(movimientoRef, nuevoMovimiento);
        transaction.update(rutaRef, rutaUpdates);

        return {
          nuevoMovimiento,
          saldoNuevo,
        };
      });

      await guardarClienteEnFirebase(
        {
          datosCliente: datosClienteNormalizados,
          imageTiendaUrl,
          imageUrl,
        },
        usuarioRuta,
        fechaInicialState,
        setInfoClientes,
        infoClientes,
      );

      setUsuarioRuta((previousUsuarioRuta) =>
        previousUsuarioRuta
          ? aplicarMovimientoARutaLocal(
              previousUsuarioRuta,
              resultadoTransaccion.nuevoMovimiento,
              resultadoTransaccion.saldoNuevo,
            )
          : previousUsuarioRuta,
      );

      // El prestamo inicial tambien escribe un movimiento de caja.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.movimientos(usuarioRuta.adminUid, usuarioRuta.uid),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.movimientosDelDiaByRuta(usuarioRuta.adminUid, usuarioRuta.uid),
      });

      setisModalCreateClienteNew(false);
      toast.success("Cliente agregado con exito");
    } catch (error) {
      console.error("Error al crear cliente nuevo:", error);
      toast.error("No se pudo agregar el cliente");
    } finally {
      setIsSubmiting(false);
    }
  };

  const campos = [
    {
      icono: <HiOutlineIdentification size={24} />,
      placeholder: "CPF",
      valor: datosCliente.cpf,
      onChange: (event) => setDatosCliente((current) => ({ ...current, cpf: event.target.value })),
    },
    {
      icono: <AiOutlineUser size={24} />,
      placeholder: "Nombre del Cliente",
      valor: datosCliente.nombreCliente,
      onChange: (event) =>
        setDatosCliente((current) => ({
          ...current,
          nombreCliente: event.target.value,
        })),
    },
    {
      icono: <FaRoute size={24} />,
      placeholder: "Direccion de Cobro",
      valor: datosCliente.direccionCobro,
      onChange: (event) =>
        setDatosCliente((current) => ({
          ...current,
          direccionCobro: event.target.value,
        })),
    },
    {
      icono: <BsTelephone size={24} />,
      placeholder: "Telefono",
      valor: datosCliente.telefono,
      onChange: (event) =>
        setDatosCliente((current) => ({
          ...current,
          telefono: event.target.value,
        })),
    },
    {
      icono: <FaRegAddressBook size={24} />,
      placeholder: "Direccion de Residencia",
      valor: datosCliente.direccion,
      onChange: (event) =>
        setDatosCliente((current) => ({
          ...current,
          direccion: event.target.value,
        })),
    },
    {
      icono: <IoStorefrontOutline size={24} />,
      placeholder: "Descripcion de la tienda",
      valor: datosCliente.descripcion,
      onChange: (event) =>
        setDatosCliente((current) => ({
          ...current,
          descripcion: event.target.value,
        })),
    },
    {
      icono: <IoLocationOutline size={24} />,
      placeholder: "Ubicacion Actual",
      valor: datosCliente.ubicacion,
      onChange: (event) =>
        setDatosCliente((current) => ({
          ...current,
          ubicacion: event.target.value,
        })),
      onClick: obtenerUbicacionActual,
      readOnly: true,
    },
  ];

  return (
    <div className="w-full min-h-screen h-screen z-20 bg-black bg-opacity-50 px-2 md:px-8 flex justify-center items-center fixed ">
      {selectorFotoActivo ? (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-[320px] rounded-md p-4 flex flex-col gap-3">
            <h3 className="text-center font-semibold text-lg">
              {selectorFotoActivo === "cliente" ? "Imagen cliente" : "Imagen tienda"}
            </h3>

            <button
              type="button"
              className="bg-[#8131bd] text-white px-3 py-2 rounded-md"
              onClick={tomarFoto}
            >
              Tomar foto
            </button>

            <button
              type="button"
              className="bg-gray-200 text-gray-900 px-3 py-2 rounded-md border border-gray-400"
              onClick={cargarDesdeGaleria}
            >
              Cargar de galeria
            </button>

            <button
              type="button"
              className="text-sm text-red-600 underline"
              onClick={() => setSelectorFotoActivo(null)}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : null}

      <div className="bg-gray-200 w-[400px] p-4 rounded-md relative max-h-[90%] overflow-y-auto">
        <IoIosClose
          className="absolute right-0 top-0 cursor-pointer"
          size={40}
          onClick={() => setisModalCreateClienteNew(false)}
        />
        <h2 className="text-center text-xl font-semibold">Crear Cliente Nuevo</h2>

        <form
          onSubmit={crearCliente}
          onInvalidCapture={mostrarMensajeCampoObligatorio}
          onInputCapture={limpiarMensajeCampoObligatorio}
          onChangeCapture={limpiarMensajeCampoObligatorio}
          className="w-full mt-2"
        >
          <fieldset disabled={isSubmiting} className="w-full flex flex-col gap-2 items-center">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Selected"
                className="h-[200px] w-auto rounded-r-md object-cover"
              />
            ) : null}

            <div className="flex w-full h-[40px] border border-gray-400 rounded-md relative">
              <button
                type="button"
                className="h-full w-[40px] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400 cursor-pointer"
                onClick={() => abrirSelectorFoto("cliente")}
              >
                <HiOutlinePhotograph size={24} />
              </button>
              <input
                ref={imageGalleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
              <input
                ref={imageCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleImageChange}
              />
              <input
                type="text"
                className="flex-1 rounded-r-md w-full px-2 focus:border-transparent focus:outline-none"
                placeholder={imagePreview ? "Cambiar Foto" : "Agregar Foto del Cliente"}
                onClick={() => abrirSelectorFoto("cliente")}
                readOnly
              />
            </div>

            {imagePreview || datosCliente.image ? (
              <button
                type="button"
                className="self-end text-sm text-red-600 underline"
                onClick={handleRemoveImage}
              >
                Quitar foto del cliente
              </button>
            ) : null}

            {imageTiendaPreview ? (
              <img
                src={imageTiendaPreview}
                alt="Selected"
                className="h-[200px] w-auto rounded-r-md object-cover"
              />
            ) : null}

            <div className="flex w-full h-[40px] border border-gray-400 rounded-md relative">
              <button
                type="button"
                className="h-full w-[40px] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400 cursor-pointer"
                onClick={() => abrirSelectorFoto("tienda")}
              >
                <HiOutlinePhotograph size={24} />
              </button>
              <input
                ref={imageTiendaGalleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageTiendaChange}
              />
              <input
                ref={imageTiendaCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleImageTiendaChange}
              />
              <input
                type="text"
                className="flex-1 rounded-r-md w-full px-2 focus:border-transparent focus:outline-none"
                placeholder={imageTiendaPreview ? "Cambiar Foto" : "Agregar Foto de Tienda"}
                onClick={() => abrirSelectorFoto("tienda")}
                readOnly
              />
            </div>

            {imageTiendaPreview || datosCliente.imageTienda ? (
              <button
                type="button"
                className="self-end text-sm text-red-600 underline"
                onClick={handleRemoveImageTienda}
              >
                Quitar foto de tienda
              </button>
            ) : null}

            {campos.map((campo, index) => (
              <CampoEntrada key={index} {...campo} />
            ))}

            <h2 className="text-center text-xl font-semibold">Datos del Credito</h2>

            <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
              <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                Fecha Inicial
              </div>
              <input
                type="date"
                className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                onChange={handleFechaInicialChange}
                defaultValue={fechaInicialState}
              />
            </div>

            <div
              className={`${
                datosCliente.fechaFinal ? "flex" : "hidden"
              } w-full h-[40px] border border-gray-400 rounded-md`}
            >
              <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                Fecha Final
              </div>
              <input
                type="text"
                className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                value={datosCliente.fechaFinal}
                readOnly
              />
            </div>

            <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
              <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                Valor Prestamo
              </div>
              <input
                type="number"
                className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                onChange={handleValorPrestamoChange}
                required
              />
            </div>

            <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
              <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                Porcentaje de interes
              </div>
              <div className="relative flex-1 w-[50%]">
                <input
                  type="number"
                  className="w-full h-full rounded-r-md px-2 pr-8 focus:border-transparent focus:outline-none"
                  onChange={handlePorcentajeInteresChange}
                  required
                  step="0.1"
                />
                <FaPercent
                  className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                  size={14}
                />
              </div>
            </div>

            <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
              <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                Cuotas Pactadas
              </div>
              <input
                type="number"
                className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                onChange={handleCuotasPactadasChange}
                required
              />
            </div>

            <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
              <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                Valor Cuota
              </div>
              <input
                type="number"
                className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                value={datosCliente.pagoDiario}
                readOnly
              />
            </div>

            <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
              <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                Forma de pago
              </div>
              <select
                className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                value={datosCliente.formaDePago}
                onChange={handleFormaDePagoChange}
              >
                {OPCIONES_FORMA_PAGO.map((opcion) => (
                  <option key={opcion} value={opcion}>
                    {opcion.charAt(0).toUpperCase() + opcion.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              className="bg-[#8131bd] w-fit text-white px-2 py-1 rounded-md flex justify-center items-center min-w-[80px]"
              disabled={isSubmiting}
            >
              {isSubmiting ? <MoonLoader size={20} color="#ffffff" /> : "Agregar Cliente"}
            </button>
          </fieldset>
        </form>
      </div>
    </div>
  );
};
